import Transformer from "@/utils/transformer"
import apiClient, { removeValuesWithObjectType } from "@/utils/cb-client"
import BulkApi from "@/utils/bulk-api";
import BulkApiHelper from "@/utils/bulk-api-helper";
import PaymentHelper from "./payment-helper";
import LogTracker from '@/utils/log-helper';
export default class CheckoutSubmitter {
  constructor(state, getters, dispatch) {
    this.state = state;
    this.getters = getters;
    this.dispatch = dispatch;
    this.bulkApiRequest = new BulkApi(this.state.auth.hpToken)
    BulkApiHelper.setConfig(state, getters)
  }

  sanitizeObj(obj) {
    const sanitizedObj = removeValuesWithObjectType(obj);
    sanitizedObj.hp_token = this.state.auth.hpToken;
    return sanitizedObj;
  }

  // if postAction is true then it return final API data
  submitCheckoutNew(postAction = false) {
    BulkApiHelper.setAsPostAction(this.bulkApiRequest, postAction);
    this.bulkApiRequest.addBulkLogs(this.getLogs());
    // Checkout new for existing customer
    if (this.state.auth.authenticated) {
      BulkApiHelper.checkCustomerDetailsModified(this.bulkApiRequest, postAction);
      // Checkout new Items for existing customer
      if(this.getters.is_items_model) return this.submitCheckoutNewItemsForCust(postAction);
      this.bulkApiRequest.addRequest(this.createSubscriptionForCustomer(postAction))

      return apiClient.generic.bulk({ hp_token: this.state.auth.hpToken }, this.bulkApiRequest.toJSON());
    } else {
      // Checkout new Items
      if(this.getters.is_items_model) return this.submitCheckoutNewForItems(postAction);
      // Checkout new for new customer
      return this.createSubscription(postAction);
    }
  }

  submitCheckoutOneTime(postAction = false) {
    BulkApiHelper.setAsPostAction(this.bulkApiRequest, postAction);
    this.bulkApiRequest.addBulkLogs(this.getLogs());
    if (this.state.auth.authenticated) {
      BulkApiHelper.checkCustomerDetailsModified(this.bulkApiRequest, true);
       
      if(this.getters.is_items_model) {
        this.bulkApiRequest.addRequest(this.createInvoiceItemsForCustomer(postAction))
        return apiClient.generic.bulk({ hp_token: this.state.auth.hpToken }, this.bulkApiRequest.toJSON());
      }
      this.bulkApiRequest.addRequest(this.createInvoiceForCustomer(postAction))
      return apiClient.generic.bulk({ hp_token: this.state.auth.hpToken }, this.bulkApiRequest.toJSON());
    } else {
      this.bulkApiRequest.addRequest(this.createCustomer(false, postAction))
      if(this.getters.is_items_model) {
        this.bulkApiRequest.addRequest(this.createInvoiceItems(postAction))
        return apiClient.generic.bulk({ hp_token: this.state.auth.hpToken }, this.bulkApiRequest.toJSON());
      }
      this.bulkApiRequest.addRequest(this.createInvoice(postAction));
      return apiClient.generic.bulk({ hp_token: this.state.auth.hpToken }, this.bulkApiRequest.toJSON());
    }
  }

  getCustomerId() {
    const contentId = this.bulkApiRequest.getRequestIdByApiName("create_customer")
    return BulkApi.getResponseKey(contentId, 'customer.id');
  }

  getPaymentMethodKey() {
    let pm = this.state.order.payment_method;

    if(!pm || this.is_payless_checkout || PaymentHelper.isOfflinePaymentMethod(pm.type)) return; // Checkout without pm details

    if(this.state.customer.payment_intent) {
      return "payment_intent"
    }
    if(pm.type == "card" && !pm.tmp_token) {
      return "card";
    }
    else if(pm.type == "direct_debit" && !pm.tmp_token) {
      return "bank_account";
    }
    else {
      return "payment_method";
    }
  }

  createCustomer(includePM, postAction = false) {
    // customer billing address
    let params = Object.assign({}, Transformer.flatten(this.state.customer.billing_address, 'billing_address'))
    if(includePM && this.state.order.payment_method && !PaymentHelper.isOfflinePaymentMethod(this.state.order.payment_method.type)) {
      const pmKey = this.getPaymentMethodKey();
      // payment method data
      if (pmKey == "payment_intent" || postAction) {
          params["customer_without_pm"] = true;
      } else if(pmKey) {
        Object.assign(params, Transformer.flatten(this.state.order.payment_method, pmKey))
      }
    } else {
      params["customer_without_pm"] = true;
    }

    if(Transformer.isCustomerDirectDebit(this.state) == true){
      params['allow_direct_debit'] = true ;
    }
    
    // customer data with custom fields
    let customerData = this.state.customer.data;
    /**
     * customer.data : vat_number, vat_number_prefix, entity_identifier_scheme 
     * customer.entity_identifiers : entity_identifiers array  
    */
    /* Einvoice entity-identifiers array */
    let entityIdentifiers = Transformer.transformEinvoiceSchemesCheckout(this.state.customer.entity_identifiers);
    Object.assign(customerData, entityIdentifiers);

    // currency code
    customerData.preferred_currency_code = this.getters.currency_code;
    
    Object.keys(customerData).forEach(key => params[key] = customerData[key]);

    if(this.getters.is_payless_checkout) {
      params["payless_checkout"] = true;
    }

    const bulkAction = apiClient.customers.create_bulk_action(this.sanitizeObj(params));
    return BulkApi.setRequestApiName(bulkAction, 'create_customer')
  }

  createSubscriptionForItems(postAction = false) {
    let params = {}
    Object.assign(params, Transformer.createSubscriptionForItems(this.state, true, postAction))
    return apiClient.customers.create_sub_for_items_bulk_action(this.sanitizeObj(params));
  }

  createSubscriptionItemsForCust(postAction = false) {
    let params = {
      hp_token: this.state.auth.hpToken,
      id: this.getters.customer_id
    }

    if(BulkApiHelper.shouldIncludePmId()) {
      const pmId = BulkApiHelper.getPaymentMethodId(this.bulkApiRequest)
      if(pmId && !BulkApiHelper.checkIfCustomersPrimaryPaymentMethod(pmId)) params.payment_source_id = pmId
    }

    Object.assign(params, Transformer.createSubscriptionForItems(this.state, false, postAction))

    return apiClient.customers.create_sub_for_items_auth_bulk_action({id: params.id}, this.sanitizeObj(params))
  }

  submitCheckoutNewForItems(postAction = false) {
    const createCustomerAction = this.createCustomer(true, postAction);
    const createSubForItemsAction = this.createSubscriptionForItems(postAction)

    BulkApi.addUrlParam(
      createCustomerAction, createSubForItemsAction, 
      'customer_id', 'customer.id'
    );
    this.bulkApiRequest
      .addRequest(createCustomerAction)
      .addRequest(createSubForItemsAction)

    return apiClient.generic.bulk({ hp_token: this.state.auth.hpToken }, this.bulkApiRequest.toJSON());
  }

  submitCheckoutNewItemsForCust(postAction = false) {
    this.bulkApiRequest.addRequest(this.createSubscriptionItemsForCust(postAction))
    return apiClient.generic.bulk({ hp_token: this.state.auth.hpToken }, this.bulkApiRequest.toJSON());
  }

  // Create subscription for existing customer
  createSubscriptionForCustomer(postAction = false) {
    const params = {
      id: this.getters.customer_id
    }
    Object.assign(params, Transformer.createSubscriptionForCustomer(this.state, false, postAction));

    if(BulkApiHelper.shouldIncludePmId()) {
      const pmId = BulkApiHelper.getPaymentMethodId(this.bulkApiRequest)
      if(pmId && !BulkApiHelper.checkIfCustomersPrimaryPaymentMethod(pmId)) params.payment_source_id = pmId
    }

    return apiClient.customers.create_subscription_bulk_action(this.sanitizeObj(params))
  }

  getInvoiceParams() {
    const params = {}
    if(this.getters.preferred_currency_code != undefined) {
      params.currency_code = this.getters.preferred_currency_code
    }
    if(this.getters.retain_pm != undefined) {
      params.retain_payment_source = this.getters.retain_pm;
    }
    if(this.getters.replace_pm != undefined) {
      params.replace_primary_payment_source = this.getters.replace_pm;
    }
    return params
  }

  createInvoice(postAction = false) {
    const params = this.getInvoiceParams();
    params.customer_id = this.getCustomerId();
    Object.assign(params, Transformer.createInvoice(this.state, this.getters, postAction));
    return apiClient.customers.create_invoice_bulk_action(this.sanitizeObj(params))
  }

  createInvoiceForCustomer(postAction = false) {
    const params = this.getInvoiceParams();
    params.customer_id = this.getters.customer_id;
    Object.assign(params, Transformer.createInvoiceForCustomer(this.state, false, this.getters, postAction));
    if(!params.payment_source_id) {
      const pmKey = this.getPaymentMethodKey();
        // payment method data
      if (pmKey == "payment_intent" || postAction) {
        params["payment_intent[id]"] = postAction ? "" : this.state.customer.payment_intent.id;
        params["payment_intent[skip_txn_consumption]"] = true;
      } else if(pmKey) {
        Object.assign(params, Transformer.flatten(this.state.order.payment_method, pmKey))
      }
    }
    return apiClient.customers.create_invoice_bulk_action(this.sanitizeObj(params))
  }

  createInvoiceItems(postAction = false) {
    const params = this.getInvoiceParams();
    params.customer_id = this.getCustomerId();
    Object.assign(params, Transformer.createInvoiceItems(this.state, this.getters, postAction))
    return apiClient.customers.create_invoice_for_items_bulk_action(this.sanitizeObj(params))
  }

  createInvoiceItemsForCustomer(postAction = false) {
    const params = this.getInvoiceParams();
    params.customer_id = this.getters.customer_id;
    Object.assign(params, Transformer.createInvoiceItemsForCustomer(this.state, false, this.getters, postAction))
    if(!params.payment_source_id || postAction) {
      const pmKey = this.getPaymentMethodKey();
        // payment method data
      if (pmKey == "payment_intent" || postAction) {
        params["payment_intent[id]"] = postAction ? "" : this.state.customer.payment_intent.id;
        params["payment_intent[skip_txn_consumption]"] = true;
      } else if(pmKey) {
        Object.assign(params, Transformer.flatten(this.state.order.payment_method, pmKey))
      }
    }    
    return apiClient.customers.create_invoice_for_items_bulk_action(this.sanitizeObj(params))
  }

  // Create subscription for new customer
  createSubscription(postAction = false) {
    const payload = Transformer.createSubscription(this.state, postAction);
    return apiClient.subscriptions.create(
      {
        hp_token: this.state.auth.hpToken
      },
      payload
    );
  }

  // if postAction is true then this function will return final API data
  submitCheckoutExisting(postAction = false) {
    BulkApiHelper.checkCustomerDetailsModified(this.bulkApiRequest, postAction);

    this.bulkApiRequest.addBulkLogs(this.getLogs());
    if(!PaymentHelper.isOfflinePaymentMethod(this.getters.selected_payment_method.type)) {
      this.bulkApiRequest.addRequest(this.overrideBillingProfile())
    }
    if(this.getters.is_items_model) {
      this.bulkApiRequest.addRequest(this.updateSubscriptionForItems(postAction))
    } else {
      this.bulkApiRequest.addRequest(this.updateSubscription(postAction))
    }
    BulkApiHelper.setAsPostAction(this.bulkApiRequest, postAction);
    return apiClient.generic.bulk({ hp_token: this.state.auth.hpToken }, this.bulkApiRequest.toJSON());
  }

  overrideBillingProfile() {
    if (!BulkApiHelper.shouldIncludePmId()) return;
    const subscriptionId = this.getters.subscription_handle;

    let params = {
      id: subscriptionId
    }
    const pmId = BulkApiHelper.getPaymentMethodId(this.bulkApiRequest)
    // Check if subscription payment id and attached payment source id is same, then skip override billing profile.
    if (pmId && BulkApiHelper.checkIfSubsAttachedPaymentMethod(pmId)) return;
    
    if (pmId && !BulkApiHelper.checkIfCustomersPrimaryPaymentMethod(pmId)) {
      params['payment_source_id'] = pmId;
    }
    return apiClient.subscriptions.override_billing_profile_bulk_action(params);
  }

  updateSubscription(postAction = false) {
    const params = Transformer.updateSubscription(this.state, postAction);
    const subscriptionId = this.getters.subscription_handle;
    params.id = subscriptionId;
    
    return apiClient.subscriptions.update_bulk_action(params)
  }

  updateSubscriptionForItems(postAction = false) {
    const params = Transformer.updateSubscriptionForItems(this.state, postAction);
    const subscriptionId = this.getters.subscription_handle;
    params.id = subscriptionId;
    return apiClient.subscriptions.update_for_items_bulk_action(params)
  }


  getLogs() {
    new LogTracker(this.getters, this.dispatch).logUserExit();
    return this.getters.get_bulk_logs;
  }
}
